/**
 * Clean Mobile Cart Handler
 * Only prevents cart drawer from opening when clicking "Add to Cart" button on mobile
 * Leaves all other cart functionality (cart icon, etc.) untouched
 */

(function () {
    'use strict';

    const MOBILE_BREAKPOINT = 767;
    const NOTIFICATION_DURATION = 3000;

    function isMobileDevice() {
        return window.innerWidth <= MOBILE_BREAKPOINT;
    }

    function showNotification() {
        const notification = document.querySelector('.cart_notification-js');
        if (notification) {
            notification.classList.add('active');
            setTimeout(() => {
                notification.classList.remove('active');
            }, NOTIFICATION_DURATION);
        }
    }

    function updateCartIcon(itemCount) {
        const cartIcon = document.querySelector('#cart-icon-bubble .cart-wrapper');
        if (cartIcon && itemCount) {
            cartIcon.textContent = `(${itemCount})`;
        }

        const cartCountElements = document.querySelectorAll('.cart_count-js');
        cartCountElements.forEach(element => {
            element.textContent = itemCount || 0;
        });
    }

    function handleMobileAddToCart(form) {
        const formData = new FormData(form);
        
        // Request cart drawer sections to update the cart
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && cartDrawer.getSectionsToRender) {
            formData.append(
                'sections',
                cartDrawer.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
        }

        return fetch('/cart/add.js', {
            method: 'POST',
            body: formData,
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
        })
            .then(response => response.json())
            .then(data => {
                console.log('Mobile: Item successfully added to cart:', data);

                // Show notification only after successful addition
                showNotification();

                // Update cart icon with actual count
                updateCartIcon(data.item_count);
                
                // Update cart drawer if sections are available
                if (cartDrawer && data.sections) {
                    cartDrawer.renderContents(data);
                }

                // Publish cart update event for other components
                if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                    publish(PUB_SUB_EVENTS.cartUpdate, {
                        source: 'mobile-cart-handler',
                        productVariantId: formData.get('id'),
                        cartData: data,
                    });
                }

                return data;
            })
            .catch(error => {
                console.error('Mobile: Cart addition failed:', error);
                throw error;
            });
    }

    function initializeMobileCartHandler() {
        console.log('Mobile cart handler initialized');

        // Store original functions for temporary override
        let originalOpenGokwikSideCart = null;
        let originalAddItemtoCartFromForm = null;
        let isProcessingMobileCart = false;

        // Target only the specific "Add to Cart" buttons on mobile
        document.addEventListener('click', function (e) {
            // Only handle on mobile
            if (!isMobileDevice()) return;

            // Only handle if it's an "Add to Cart" button inside add-to-bag-col
            const button = e.target.closest('.product-form__submit-js');
            const isAddToBagButton = button && button.closest('.add-to-bag-col');

            if (!isAddToBagButton) return;

            // Prevent multiple simultaneous processing
            if (isProcessingMobileCart) {
                console.log('Mobile: Already processing cart addition, ignoring click');
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                return false;
            }

            console.log('Mobile: Add to Cart button clicked - preventing cart drawer');
            isProcessingMobileCart = true;

            // Prevent default form submission
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            // Temporarily override external cart functions to prevent cart opening
            if (window.openGokwikSideCart && !originalOpenGokwikSideCart) {
                originalOpenGokwikSideCart = window.openGokwikSideCart;
                window.openGokwikSideCart = function () {
                    console.log('Mobile: Blocked openGokwikSideCart during add to cart');
                    return false;
                };
            }

            if (window.addItemtoCartFromForm && !originalAddItemtoCartFromForm) {
                originalAddItemtoCartFromForm = window.addItemtoCartFromForm;
                window.addItemtoCartFromForm = function () {
                    console.log('Mobile: Blocked addItemtoCartFromForm during add to cart');
                    return false;
                };
            }

            // Also prevent cart drawer from opening
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && cartDrawer.open) {
                const originalOpen = cartDrawer.open;
                cartDrawer.open = function () {
                    console.log('Mobile: Blocked cart-drawer.open() during add to cart');
                    return false;
                };

                // Restore after delay
                setTimeout(() => {
                    cartDrawer.open = originalOpen;
                    console.log('Mobile: Restored cart-drawer.open()');
                }, 2000);
            }

            // Find the form
            const form = button.closest('product-form')?.querySelector('form');
            if (!form) {
                console.error('Mobile: Could not find product form');
                return;
            }

            // Handle the cart addition
            handleMobileAddToCart(form)
                .then(() => {
                    console.log('Mobile: Cart addition completed');
                })
                .catch(error => {
                    console.error('Mobile: Failed to add item to cart:', error);
                })
                .finally(() => {
                    // Restore original functions after a delay
                    setTimeout(() => {
                        if (originalOpenGokwikSideCart) {
                            window.openGokwikSideCart = originalOpenGokwikSideCart;
                            originalOpenGokwikSideCart = null;
                            console.log('Mobile: Restored openGokwikSideCart');
                        }
                        if (originalAddItemtoCartFromForm) {
                            window.addItemtoCartFromForm = originalAddItemtoCartFromForm;
                            originalAddItemtoCartFromForm = null;
                            console.log('Mobile: Restored addItemtoCartFromForm');
                        }
                        isProcessingMobileCart = false;
                        console.log('Mobile: Cart processing completed');
                    }, 2000); // Restore after 2 seconds
                });

            return false;
        }, true); // Use capture phase to intercept early

        // Also handle form submission directly
        document.addEventListener('submit', function (e) {
            // Only handle on mobile
            if (!isMobileDevice()) return;

            // Only handle product forms with add-to-bag button
            const form = e.target.closest('product-form');
            const hasAddToBagButton = form && form.querySelector('.add-to-bag-col .product-form__submit-js');

            if (!hasAddToBagButton) return;

            console.log('Mobile: Product form submitted - preventing cart drawer');

            // Prevent default form submission
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            // Temporarily disable external cart functions
            const originalFunctions = {};
            if (window.openGokwikSideCart) {
                originalFunctions.openGokwikSideCart = window.openGokwikSideCart;
                window.openGokwikSideCart = () => console.log('Mobile: Blocked openGokwikSideCart during form submit');
            }
            if (window.addItemtoCartFromForm) {
                originalFunctions.addItemtoCartFromForm = window.addItemtoCartFromForm;
                window.addItemtoCartFromForm = () => console.log('Mobile: Blocked addItemtoCartFromForm during form submit');
            }

            // Handle the cart addition
            handleMobileAddToCart(e.target)
                .catch(error => {
                    console.error('Mobile: Failed to add item to cart:', error);
                })
                .finally(() => {
                    // Restore functions after delay
                    setTimeout(() => {
                        Object.keys(originalFunctions).forEach(key => {
                            window[key] = originalFunctions[key];
                            console.log(`Mobile: Restored ${key}`);
                        });
                    }, 2000);
                });

            return false;
        }, true); // Use capture phase

        // Add global prevention for external cart functions during mobile processing
        const originalGlobalFunctions = {};

        function preventExternalCartFunctions() {
            if (!originalGlobalFunctions.openGokwikSideCart && window.openGokwikSideCart) {
                originalGlobalFunctions.openGokwikSideCart = window.openGokwikSideCart;
                window.openGokwikSideCart = function () {
                    if (isMobileDevice() && isProcessingMobileCart) {
                        console.log('Mobile: Globally blocked openGokwikSideCart');
                        return false;
                    }
                    return originalGlobalFunctions.openGokwikSideCart.apply(this, arguments);
                };
            }

            if (!originalGlobalFunctions.addItemtoCartFromForm && window.addItemtoCartFromForm) {
                originalGlobalFunctions.addItemtoCartFromForm = window.addItemtoCartFromForm;
                window.addItemtoCartFromForm = function () {
                    if (isMobileDevice() && isProcessingMobileCart) {
                        console.log('Mobile: Globally blocked addItemtoCartFromForm');
                        return false;
                    }
                    return originalGlobalFunctions.addItemtoCartFromForm.apply(this, arguments);
                };
            }
        }

        // Set up global prevention with retry mechanism
        preventExternalCartFunctions();
        setTimeout(preventExternalCartFunctions, 500);
        setTimeout(preventExternalCartFunctions, 1000);
        setTimeout(preventExternalCartFunctions, 2000);

        // Add resize listener for dynamic mobile/desktop switching
        let resizeTimeout;
        window.addEventListener('resize', function () {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                const mode = isMobileDevice() ? 'mobile' : 'desktop';
                console.log(`Screen resized - now in ${mode} mode`);
            }, 250);
        });
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeMobileCartHandler);
    } else {
        initializeMobileCartHandler();
    }

})();
